import { Grid } from '@material-ui/core';
import { Button, SelectField, TextField } from '@roc/ui';
import { observer } from 'mobx-react';
import { useMemo, useState } from 'react';
import { delinquencyService } from '../delinquencyService';
import type { DelinquencyConstants } from './delinquencyConstants';

export type DelinquencyStatusFormData = {
  comments: string;
  delinquencyStatus: string;
  delinquencySubStatus: string;
};

export type DelinquencyStatusFormProps = {
  handleClose: () => void;
  loanId?: number;
  loanDocument?: Record<string, unknown>;
  onSave?: (data: DelinquencyStatusFormData) => void | Promise<void>;
  statusValues?: DelinquencyConstants;
};

export const DelinquencyStatusForm = observer(
  ({ handleClose, loanId, loanDocument, onSave, statusValues = {} }: DelinquencyStatusFormProps) => {
    const [comments, setComments] = useState('');
    const [delinquencyStatus, setDelinquencyStatus] = useState<string>('');
    const [delinquencySubStatus, setDelinquencySubStatus] = useState<string>('');

    const mainOptions = useMemo(
      () => Object.keys(statusValues).map((label) => ({ label, value: label })),
      [statusValues]
    );

    const subOptions = useMemo(() => {
      const sub = statusValues[delinquencyStatus];
      if (!sub || sub.length === 0) return [];
      return sub.map((label) => ({ label, value: label }));
    }, [statusValues, delinquencyStatus]);

    const showSubDropdown = subOptions.length > 0;

    const handleMainStatusChange = (val: string) => {
      setDelinquencyStatus(val);
      setDelinquencySubStatus('');
    };

    const handleSubmit = async () => {
      const data = { comments, delinquencyStatus, delinquencySubStatus };
      if (loanId != null) {
        await delinquencyService.updateDelinquencyStatusAndComments({
          loanId,
          delinquencyComments: comments,
          delinquencyStatus,
          delinquencySubStatus,
          delinquencyStatusComment: comments,
        });
      }
      if (onSave) {
        await onSave(data);
      }
      handleClose();
    };

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            variant="outlined"
            label="Comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            fullWidth
            multiline
            minRows={8}
            margin="normal"
            testId="delinquency-status-comments"
          />
        </Grid>
        <Grid item xs={12}>
          <SelectField
            label="Delinquency Status"
            value={delinquencyStatus}
            options={mainOptions}
            onChange={(val) => handleMainStatusChange(String(val))}
            disabled={mainOptions.length === 0}
            testId="delinquency-status-select"
            fullWidth
            variant="outlined"
            margin="normal"
          />
        </Grid>
        {showSubDropdown && (
          <Grid item xs={12}>
            <SelectField
              label="Delinquency SubReport"
              value={delinquencySubStatus}
              options={subOptions}
              onChange={(val) => setDelinquencySubStatus(String(val))}
              testId="delinquency-substatus-select"
              fullWidth
              variant="outlined"
              margin="normal"
            />
          </Grid>
        )}
        <Grid container spacing={2} justifyContent="center">
          <Grid item>
            <Button
              type="button"
              variant="contained"
              color="default"
              testId="delinquency-status-cancel"
              onClick={handleClose}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              type="button"
              variant="contained"
              color="primary"
              testId="delinquency-status-submit"
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );
  }
);
